import type { Disclosure } from "@msuite/ui/hooks";
import { createContext, useContext } from "react";
import type { DailyPlan, GuestPlan } from "./use-daily";

/** Interface */
interface IDailyContext {
	dayIndex: number;
	disclosure: Disclosure;
	showTicketsDisclosure: Disclosure;
	vehicleDisclosure: Disclosure;
	date: string;
	isInteractionBlocked: boolean;
	/**
	 * Whether the caller holds `update Plan` at all, separate from `isInteractionBlocked` because
	 * the two answer different questions: a published day blocks arranging for everyone and is
	 * undone by publishing it back, while this one says whether the caller may touch a board in
	 * the first place. The publish control itself hangs off this rather than off the block, or
	 * nobody could ever pull a day back.
	 */
	canArrangeBoard: boolean;
	/**
	 * Whether the caller may place, reorder or remove this project's work on the day — `update
	 * Plan` or the project relationship (members, active substitutes, admins), with the
	 * published lock folded in. Per project, because the relationship arm binds the ticket's
	 * project; a `canArrangeBoard` holder is covered through the plan arm.
	 */
	canArrangeTicketOf: (projectId: string) => boolean;
	/**
	 * Where the rule list stands, so "not yet" can look different from "no". `canArrangeBoard` is
	 * false while it is `loading` and while it is `error`, which is the right default for a
	 * control that writes and the wrong thing to render as a refusal.
	 */
	rulesState: "loading" | "error" | "ready";
	planId: string;
	// Either our own board for the day, or another organisation's board that carries a crew of
	// ours. The second shape is narrower — it has no vehicles of its own — and read-only except
	// for staffing the caller's own columns.
	plan: DailyPlan | GuestPlan | null;
}

/** Context */
export const DailyContext = createContext<IDailyContext>({} as IDailyContext);

/** Hooks */
export function useDailyContext() {
	return useContext(DailyContext);
}
